import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useCartStore } from '../../store/cartStore';
import LangSwitcher from '../common/LangSwitcher';
import { SHOW_CATALOG_NAV, SHOW_EVENTS } from '../../config/features';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { cart, fetchCart } = useCartStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) fetchCart();
  }, [user]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/');
  };

  const cartCount = cart?.itemCount ?? 0;

  return (
    <header style={styles.header}>
      <div style={styles.inner}>
        <Link to="/" style={styles.logo}><img src="/logo.png" alt="Moa" style={styles.logoImg} /></Link>

        <nav style={styles.nav}>
          {SHOW_CATALOG_NAV && (
            <>
              <Link to="/products" style={styles.navLink}>{t('header.products')}</Link>
              <Link to="/group-buys" style={styles.navLink}>{t('header.groupBuys')}</Link>
            </>
          )}
          <Link to="/purchase-requests" style={styles.navLink}>{t('header.proxyBuy')}</Link>
          <Link to="/faq" style={styles.navLink}>{t('header.faq')}</Link>
          <Link to="/support" style={styles.navLink}>{t('header.support')}</Link>
          {SHOW_EVENTS && (
            <div
              style={styles.eventWrap}
              onMouseEnter={() => setEventOpen(true)}
              onMouseLeave={() => setEventOpen(false)}
            >
              <Link to="/events" style={styles.navLink}>{t('header.event')}</Link>
              {eventOpen && (
                <div style={styles.eventDropdown}>
                  <Link to="/events/welcome-coupon" style={styles.eventItem} onClick={() => setEventOpen(false)}>
                    Welcome Coupon
                  </Link>
                </div>
              )}
            </div>
          )}
        </nav>

        <div style={styles.actions}>
          {user?.role === 'ADMIN' && <LangSwitcher />}

          {user ? (
            <>
              <Link to="/cart" style={{ ...styles.iconBtn, position: 'relative' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2h12l2 7H4L6 2z"/>
                  <path d="M4 9v11a2 2 0 002 2h12a2 2 0 002-2V9"/>
                  <path d="M9 13a3 3 0 006 0"/>
                </svg>
                {cartCount > 0 && <span style={styles.badge}>{cartCount}</span>}
              </Link>

              {/* User dropdown */}
              <div ref={menuRef} style={styles.menuWrap}>
                <button style={styles.iconBtn} onClick={() => setMenuOpen(o => !o)}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                </button>

                {menuOpen && (
                  <div style={styles.dropdown}>
                    <div style={styles.dropdownHeader}>
                      <p style={styles.dropdownName}>{user.name}</p>
                      <p style={styles.dropdownEmail}>{user.email}</p>
                    </div>
                    <div style={styles.dropdownDivider} />
                    <Link to="/mypage" style={styles.dropdownItem} onClick={() => setMenuOpen(false)}>
                      My Account
                    </Link>
                    <Link to="/support" style={styles.dropdownItem} onClick={() => setMenuOpen(false)}>
                      Support
                    </Link>
                    {user.role === 'ADMIN' && (
                      <Link to="/admin" style={styles.dropdownItem} onClick={() => setMenuOpen(false)}>
                        Admin
                      </Link>
                    )}
                    <div style={styles.dropdownDivider} />
                    <button style={styles.dropdownLogout} onClick={handleLogout}>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" style={styles.loginBtn}>{t('header.login')}</Link>
              <Link to="/register" style={styles.registerBtn}>{t('header.register')}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    position: 'sticky', top: 0, zIndex: 100,
    background: '#fff', borderBottom: '1px solid #e8e8e8',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  inner: {
    maxWidth: 1200, margin: '0 auto', padding: '0 20px',
    height: 64, display: 'flex', alignItems: 'center', gap: 32,
  },
  logo: { fontSize: 20, fontWeight: 700, color: '#b39ddb', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' },
  logoImg: { height: 38, display: 'block' },
  nav: { display: 'flex', gap: 24, flex: 1, alignItems: 'center' },
  navLink: { fontSize: 14, fontWeight: 500, color: '#555' },
  eventWrap: { position: 'relative', display: 'inline-flex', alignItems: 'center' },
  eventDropdown: {
    position: 'absolute', top: '100%', left: 0,
    background: '#fff', border: '1px solid #f0f0f0', borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)', minWidth: 220, padding: 6, zIndex: 300,
  },
  eventItem: { display: 'block', padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#6a1b9a', textDecoration: 'none', borderRadius: 6 },
  actions: { display: 'flex', alignItems: 'center', gap: 10 },
  iconBtn: {
    fontSize: 20, position: 'relative', display: 'flex', alignItems: 'center',
    color: '#555', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
  },
  badge: {
    position: 'absolute', top: -6, right: -8,
    background: '#b39ddb', color: '#fff', borderRadius: '50%',
    fontSize: 10, width: 18, height: 18,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  menuWrap: { position: 'relative' },
  dropdown: {
    position: 'absolute', top: 'calc(100% + 10px)', right: 0,
    background: '#fff', border: '1px solid #f0f0f0',
    borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    minWidth: 200, zIndex: 300, overflow: 'hidden',
  },
  dropdownHeader: { padding: '14px 16px 12px' },
  dropdownName: { fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 2 },
  dropdownEmail: { fontSize: 12, color: '#999' },
  dropdownDivider: { height: 1, background: '#f5f5f5' },
  dropdownItem: {
    display: 'block', padding: '11px 16px', fontSize: 13, color: '#444',
    textDecoration: 'none', cursor: 'pointer',
  },
  dropdownLogout: {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '11px 16px', fontSize: 13, color: '#e53935',
    background: 'none', border: 'none', cursor: 'pointer',
  },
  loginBtn: {
    padding: '6px 16px', border: '1px solid #b39ddb', borderRadius: 20,
    color: '#b39ddb', fontSize: 13, fontWeight: 500,
  },
  registerBtn: {
    padding: '6px 16px', background: '#b39ddb', borderRadius: 20,
    color: '#fff', fontSize: 13, fontWeight: 500,
  },
};

export default Header;
